import { useState, useEffect, useContext } from 'react'
import cn from 'classnames'
import { Typography, IconCtaArrow } from '@grammarly/badger-ui'
import { Icon, InterfaceTransformIcon } from '@grammarly/design-system'
import { SignUpToolsBanner } from '@grammarly/website-components'
import { FormattedMessage } from 'react-intl'

import { SignUpProductRecButton } from '../../../item/cta-button/signup-product-recommendation'
import { EventProps } from '../../../../lib/tracking/gnar'
import { getConfig } from '../../../../config'
import { navHelper } from '../../../../lib/services/navigation'
import { composeUrl } from '../../../../lib/utils/url'
import { PageContext } from '../../../../contexts/page-context'
import { gnarSpec } from '../../../../lib/tracking/gnar'
import { useSignUpAction } from '../../../item/cta-button/signup-product-recommendation/hooks/useSignupAction'

import styles from './footer-banner.module.scss'
import { withPublicAssetUrl } from '../../../../lib/utils/cdn'

const { TM } = Typography

interface ParaphrasingDetectorDesktopFooterProps {
  sideBySideClicked: string
  isDynamicBannerTreatment: boolean
  gnarTrackFn: (trackEventProps: EventProps) => Promise<void>
}

const config = getConfig()

// For paraphrasing and paraphrase
const paraphrasingPrefix = 'paraphras'

const ParaphrasingBanner = () => {
  const { slug } = useContext(PageContext)

  // Hardcoded for now. Add to Contentful when we add the banner content model.
  const getBannerMessage = () => {
    if (slug.startsWith(paraphrasingPrefix)) {
      return (
        <FormattedMessage
          id="goTemplatesTool.footerBanner.paraphrasingDesktopBanner.copy.forSlugWithParaphrasingPrefix"
          description="Go Templates Tool - Footer Banner - Paraphrasing Desktop Banner - Copy - For Slug With Paraphrasing Prefix"
          defaultMessage="Continue paraphrasing and access AI features"
        />
      )
    }

    return (
      <FormattedMessage
        id="goTemplatesTool.footerBanner.paraphrasingDesktopBanner.copy"
        description="Go Templates Tool - Footer Banner - Paraphrasing Desktop Banner - Copy"
        defaultMessage="Brainstorm, write, and rewrite with generative AI"
      />
    )
  }

  return (
    <div className={styles.bannerWrapper}>
      <img
        src={`${withPublicAssetUrl('go-templates-tool/enterprise-shapes.svg')}`}
        alt=""
        className={styles.bannerShapes}
      />

      <TM as="span" className={styles.bannerMessage}>
        {getBannerMessage()}
      </TM>
      <SignUpProductRecButton
        className={styles.bannerPrompt}
        text={
          <>
            <TM as="span">
              <strong>
                <FormattedMessage id="phrase.getGrammarly" description="Get Grammarly" defaultMessage="Get Grammarly" />
              </strong>
              &nbsp;
              <FormattedMessage
                id="phrase.itsFree"
                description="Sign up prompt value prop"
                defaultMessage="It's free"
              />
            </TM>
            <div className={styles.bannerPromptArrow}>
              <Icon icon={InterfaceTransformIcon} accessibilityLabel="" variant="inherit" />
            </div>
          </>
        }
        placement="Section"
      />
    </div>
  )
}

const ParaphrasingSignUpBanner = ({ gnarTrackFn }: { gnarTrackFn: (trackEventProps: EventProps) => Promise<void> }) => {
  const { pageId, slug } = useContext(PageContext)
  const onSignUpClick = useSignUpAction({ placement: 'Section' })

  const signUpUrl = composeUrl(config.paths.funnel.signup, navHelper.currentQueryParams)

  const handleSignUpClick = () => {
    gnarSpec.bannerClick('Paraphrasing Tool SignUp banner', pageId, slug, signUpUrl)
    onSignUpClick()
  }

  const signUpCTAs = [
    {
      btnText: (
        <Typography.TS as="span">
          <strong>
            <FormattedMessage id="phrase.getGrammarly" description="Get Grammarly" defaultMessage="Get Grammarly" />
          </strong>
          &nbsp;
          <FormattedMessage id="phrase.itsFree" description="Sign up prompt value prop" defaultMessage="It's free" />
        </Typography.TS>
      ),
      targetUrl: signUpUrl,
      onClick: handleSignUpClick,
      ariaLabel: "Get Grammarly It's free",
      ctaIcon: {
        iconDirection: 'right' as 'right' | 'left',
        iconComponent: <IconCtaArrow />,
      },
    },
  ]

  return (
    <SignUpToolsBanner
      clientName="cms"
      text={
        <FormattedMessage
          id="goTemplatesTool.footerBanner.paraphrasingSignUpBanner.copy"
          description="Go Templates Tool - Footer Banner - Paraphrasing Sign Up Banner - Copy"
          defaultMessage="Continue paraphrasing and access AI features"
        />
      }
      gnarTrackFn={gnarTrackFn}
      ctas={signUpCTAs}
      bannerType="Paraphrasing Tool"
      ariaLabel="Sign up banner for Paraphrasing Tool"
      delay={1500}
      animationType="slideIn"
    />
  )
}

export const ParaphrasingDesktopFooter = ({
  sideBySideClicked,
  isDynamicBannerTreatment,
  gnarTrackFn,
}: ParaphrasingDetectorDesktopFooterProps) => {
  const [showDBExperimentBanner, setShowDBExperimentBanner] = useState(false)

  useEffect(() => {
    // If user is in an experiment treatment, show banner only when `Paraphrase` button is clicked
    if (sideBySideClicked === 'clicked' && isDynamicBannerTreatment) {
      setShowDBExperimentBanner(true)
    }
  }, [sideBySideClicked, isDynamicBannerTreatment])

  const renderFooterBanner = () => {
    if (showDBExperimentBanner && isDynamicBannerTreatment) {
      return <ParaphrasingSignUpBanner gnarTrackFn={gnarTrackFn} />
    }

    if (!isDynamicBannerTreatment) {
      return <ParaphrasingBanner />
    }
    return null
  }

  return (
    <div aria-live="polite" className={cn({ [styles.dynamicBanner]: isDynamicBannerTreatment })}>
      {renderFooterBanner()}
    </div>
  )
}
